import React, {createRef, ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import ContentMode from "../ContentMode";
import {PortalGlobals} from "../Portal";
import LengthUnit from "../../shared/units/LengthUnit";
import {PortalDialogsGlobals} from "../dialog/PortalDialogs";
import DocumentFilterComponent, {DocumentFilterGlobals} from "./DocumentFilterComponent";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../document/PortalDocumentManager";
import DocumentFilter from "../document/DocumentFilter";
import DocumentBarItem from "./DocumentBarItem";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import {UploadDialogDefinition} from "../dialog/components/document/UploadDialog";
import DocumentSettings from "../document/DocumentSettings";
import MenuButton from "../../shared/menu/MenuButton";
import Immutable from "../../shared/immutable/Immutable";
import DomHelper from "../../shared/helper/DomHelper";
import DocumentSorting, {DocumentSortingGlobals} from "../document/DocumentSorting";
import {PdfaDialogDefinition} from "../dialog/components/convert/PdfaDialog";
import SidebarLeftMode from "../viewer/settings/SidebarLeftMode";
import SidebarRightMode from "../viewer/settings/SidebarRightMode";
import {Checkbox} from "primereact/checkbox";
import {MenuItem} from "primereact/menuitem";
import Translate from "../../shared/translation/Translate";
import {SidebarFunctionGlobals} from "../document/components/pdf/SidebarFunctions";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Preview from "../startscreen/Preview";
import {Tooltip} from "primereact/tooltip";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

interface DocumentBarProperties extends PortalDialogsGlobals, DocumentFilterGlobals, DocumentSortingGlobals,
	PortalDocumentManagerGlobals, PortalGlobals, I18nGlobals, SidebarFunctionGlobals, DebugGlobals {
}

interface DocumentBarStatus {
	sliderPosition: number;
	sliderNeeded: boolean;
}

enum SliderDirection {
	LEFT = "left",
	RIGHT = "right"
}

export class DocumentBarDefinition extends PortalPureComponent<DocumentBarProperties, DocumentBarStatus> {
	private readonly ITEM_WIDTH: number = 350;
	private documentSliderElement: RefObject<HTMLDivElement>;
	private documentSliderContainerElement: RefObject<HTMLDivElement>;
	private buttonsLeftElement: RefObject<HTMLDivElement>;
	private buttonsRightElement: RefObject<HTMLDivElement>;
	private resizeObserver: ResizeObserver;

	/**
	 * Constructs a {@link DocumentBar} component
	 */
	constructor(props: DocumentBarProperties) {
		super(props);

		this.state = {
			sliderPosition: 0,
			sliderNeeded: false
		};

		this.documentSliderElement = createRef<HTMLDivElement>();
		this.documentSliderContainerElement = createRef<HTMLDivElement>();
		this.buttonsLeftElement = createRef<HTMLDivElement>();
		this.buttonsRightElement = createRef<HTMLDivElement>();

		this.moveSlider = this.moveSlider.bind(this);
		this.isSliderNeeded = this.isSliderNeeded.bind(this);
		this.renderMobile = this.renderMobile.bind(this);
		this.resizeObserver = new ResizeObserver(this.isSliderNeeded);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		if (FunctionHelper.exists(this.documentSliderContainerElement.current)) {
			this.resizeObserver.observe(this.documentSliderContainerElement.current!);
		}
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		if (FunctionHelper.exists(this.documentSliderContainerElement.current)) {
			this.resizeObserver.unobserve(this.documentSliderContainerElement.current!);
		}
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<DocumentBarProperties>, prevState: Readonly<DocumentBarStatus>, snapshot?: any
	): Promise<void> {
		this.isSliderNeeded();

		if (!this.props.documentSelection.equals(prevProps.documentSelection) &&
			this.props.documentSelection.get().length === 1) {
			this.scrollToItem(this.props.documentSelection.get()[0].id!);
		}
	}

	private computeContentWidth(): number {
		let contentWidth: number = 0;
		let documentFilter: DocumentFilter = DocumentFilter.fromJson(this.props.documentFilter.get());

		for (let listItem of this.props.portalDocuments.get()) {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(listItem.id!);
			let documentSettings: DocumentSettings = portalDocument.getSettings();

			if (documentFilter.filter(portalDocument) || documentSettings.closed) {
				continue;
			}

			contentWidth += this.ITEM_WIDTH;
		}

		return contentWidth;
	}

	/**
	 * checks if the document bar slider is needed
	 */
	private isSliderNeeded(): void {
		if (!FunctionHelper.exists(this.documentSliderElement.current) ||
			!FunctionHelper.exists(this.documentSliderContainerElement.current)) {
			return;
		}

		let contentWidth: number = this.computeContentWidth();
		let sliderWidth: number = this.documentSliderContainerElement.current!.offsetWidth;

		this.setState({
			sliderNeeded: contentWidth > sliderWidth
		});
	}

	/**
	 * Move document bar slider by one document
	 */
	private moveSlider(direction: SliderDirection): void {
		if (!FunctionHelper.exists(this.documentSliderElement.current) ||
			!FunctionHelper.exists(this.documentSliderContainerElement.current)) {
			return;
		}

		let contentWidth: number = this.computeContentWidth();
		let sliderWidth: number = this.documentSliderContainerElement.current!.offsetWidth;
		let sliderPosition: number = this.state.sliderPosition;

		if (contentWidth < sliderWidth) {
			return;
		}

		let computedPos = sliderPosition;
		switch (direction) {
			case SliderDirection.RIGHT:
				computedPos -= this.ITEM_WIDTH;

				this.setState({
					sliderPosition:
						Math.abs(computedPos) + sliderWidth < contentWidth ? computedPos : sliderWidth - contentWidth
				});
				break;
			case SliderDirection.LEFT:
				computedPos += this.ITEM_WIDTH;

				this.setState({
					sliderPosition: computedPos < 0 ? computedPos : 0
				});
				break;
		}
	}

	private scrollToItem(id: string) {
		if (!FunctionHelper.exists(this.documentSliderElement.current) ||
			!FunctionHelper.exists(this.documentSliderContainerElement.current)) {
			return;
		}

		let contentWidth: number = this.computeContentWidth();
		let sliderWidth: number = this.documentSliderContainerElement.current!.offsetWidth;
		let selectedItem = DomHelper.find(this.documentSliderContainerElement.current!, "#barItem-" + id);
		let computedPos = FunctionHelper.applyValue(selectedItem?.offsetLeft, 0);

		// check if is in viewport
		if (computedPos + this.ITEM_WIDTH <= this.state.sliderPosition + sliderWidth &&
			computedPos >= Math.abs(this.state.sliderPosition)) {
			return;
		}

		computedPos *= -1;

		if (sliderWidth >= contentWidth) {
			computedPos = 0;
		} else if (Math.abs(computedPos) + sliderWidth >= contentWidth) {
			computedPos = sliderWidth - contentWidth;
		}

		this.setState({
			sliderPosition: computedPos
		});
	}

	private renderMobile(): ReactNode {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let documentFilter: DocumentFilter = DocumentFilter.fromJson(this.props.documentFilter.get());
		let documentSorting: DocumentSorting = DocumentSorting.fromJson(this.props.documentSorting.get());
		let sortedItems: Array<PortalDocumentInterface> = documentSorting.sortItems(this.props.portalDocuments.get());
		let documentSelection: Array<PortalDocumentInterface> = this.props.documentSelection.get();

		let selectedDocument: PortalDocument | undefined;
		if (documentSelection.length === 1) {
			selectedDocument = this.props.context.documentManager.findDocument(
				this.props.documentSelection.get()[0].id!
			);
		}

		let dropdownItems: Array<MenuItem> = [];
		for (let listItem of sortedItems) {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(listItem.id!);
			let documentSettings: DocumentSettings = portalDocument.getSettings();
			let tooltipId: string = DomHelper.generateUUID(true);

			if (documentFilter.filter(portalDocument)) {
				continue;
			}

			let classList: Array<string> = ["p-menuitem-link"]

			if (!portalDocument.isLocked()) {
				classList.push("preview-" + tooltipId);
			}

			dropdownItems.push({
				label: portalDocument.getFileName(),
				icon: "icon" + (!documentSettings.closed ? " icon-check" : " empty"),
				template: (
					<>
						<a href={listItem.id} className={classList.join(" ")} role="menuitem" data-pr-position={"left"}
						   onClick={async (event) => {
							   event.preventDefault();
							   let command: () => Promise<void> = async () => {
								   documentManager.open(listItem.id!);
							   }

							   if (DocumentSettings.checkActiveMouseFunction(this.props)) {
								   DocumentSettings.confirmResetMouseFunction(this.props, command);
								   return;
							   }

							   await command();
						   }}>
							<span className="p-menuitem-text" style={{
								flexGrow: 1
							}}>
								{portalDocument.getFileName()}
							</span>
							{portalDocument.isArchive() && !portalDocument.isPortfolio() &&
								<MenuButton {...this.props} icon="icon icon-archive small"
											tooltip={Translate.value("document_bar.archive", {ns: "menu"})}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentManager.open(portalDocument.getSourceDocumentId()!);

													if (PdfaDialogDefinition.isEnabled(this.props.context.info)) {
														this.props.context.getPage().setState({
															showPdfaDialog: true,
															pdfaParameter: undefined
														});
													}
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
							{portalDocument.hasSignature() && !portalDocument.isPortfolio() &&
								<MenuButton {...this.props} icon="icon icon-signature small"
											tooltip={Translate.value("document_bar.signature", {ns: "menu"})}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentSettings.sidebarLeftMode = SidebarLeftMode.SIGNATURES;
													documentManager.open(portalDocument.getSourceDocumentId()!);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
							{(portalDocument.hasPasswordProtection() || portalDocument.hasCertificateProtection()) &&
								<MenuButton {...this.props} icon="icon icon-locked"
											tooltip={
												portalDocument.hasPasswordProtection() ?
													Translate.value(
														"document_bar.security.password", {ns: "menu"}
													) :
													portalDocument.hasCertificateProtection() ?
														Translate.value(
															"document_bar.security.certificate", {ns: "menu"}
														) :
														undefined
											}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentSettings.sidebarRightMode = SidebarRightMode.SECURITY;
													documentManager.open(portalDocument.getSourceDocumentId()!);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
							{portalDocument.getAttachments().length > 0 && !portalDocument.isPortfolio() &&
								<MenuButton {...this.props} icon="icon icon-attachment small"
											tooltip={Translate.value(
												"document_bar.attachment", {ns: "menu"}
											)}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentSettings.sidebarLeftMode = SidebarLeftMode.ATTACHMENTS;
													documentManager.open(portalDocument.getSourceDocumentId()!);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
						</a>
						{
							(
								(this.props.contentMode === ContentMode.VIEWER && !documentSettings.selected) ||
								this.props.contentMode !== ContentMode.VIEWER
							) &&
							<Tooltip target={".preview-" + tooltipId} className="tooltip-preview delay"
									 appendTo={document.getElementById("root")}>
								<div className="tooltip-content">
									<Preview {...this.props} portalDocument={Immutable.from(listItem)}/>
								</div>
							</Tooltip>
						}
					</>
				)
			});
		}

		return (
			<div className="document-bar-mobile" data-help="global.menu.documents" data-position="bottom">
				<div className="group left">
					<MenuButton {...this.props} icon="icon icon-home"
								tooltip={Translate.value("document_bar.home", {ns: "menu"})}
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										this.props.context.getPage().setState({
											contentMode: ContentMode.STARTSCREEN
										});
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}
								className={this.props.contentMode === ContentMode.STARTSCREEN ? "active" : ""}/>
					<MenuButton {...this.props} icon="icon icon-upload small"
								disabled={!UploadDialogDefinition.isEnabled(this.props.context.info)}
								onClick={async () => {
									let command: () => Promise<void> = async () => {
										this.props.context.getPage().setState({showUploadDialog: true})
									}

									if (DocumentSettings.checkActiveMouseFunction(this.props)) {
										DocumentSettings.confirmResetMouseFunction(this.props, command);
										return;
									}

									await command();
								}}
								tooltip={Translate.value("document_bar.upload", {ns: "menu"})}/>
				</div>
				<div className="group right">
					<DocumentFilterComponent {...this.props}/>
				</div>
				<div className="document">
					{dropdownItems.length > 0 &&
						<MenuButton {...this.props} splitButton={true} menuItems={dropdownItems}
									label={selectedDocument?.getDocumentTitle()}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											if (!FunctionHelper.exists(selectedDocument)) {
												return;
											}

											documentManager.open(selectedDocument?.getSourceDocumentId()!);
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}/>
					}
				</div>
			</div>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let documentFilter: DocumentFilter = DocumentFilter.fromJson(this.props.documentFilter.get());
		let documentSorting: DocumentSorting = DocumentSorting.fromJson(this.props.documentSorting.get());
		let sortedItems: Array<PortalDocumentInterface> = documentSorting.sortItems(this.props.portalDocuments.get());
		let documentSelection: Array<PortalDocumentInterface> = this.props.documentSelection.get();

		let items: Array<ReactNode> = [];
		let dropdownItems: Array<MenuItem> = [];

		for (let listItem of sortedItems) {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(listItem.id!);
			let documentSettings: DocumentSettings = portalDocument.getSettings();
			let tooltipId: string = DomHelper.generateUUID(true);

			if (documentFilter.filter(portalDocument)) {
				continue;
			}

			if (!documentSettings.closed) {
				items.push(
					<DocumentBarItem {...this.props} id={"barItem-" + listItem.id} key={listItem.id}
									 portalDocument={Immutable.from(listItem)}/>
				);
			}

			let classList: Array<string> = ["p-menuitem-link"]

			if (!portalDocument.isLocked()) {
				classList.push("preview-" + tooltipId);
			}

			dropdownItems.push({
				label: portalDocument.getFileName(),
				className: documentSettings.selected ? " active" : "",
				icon: "icon" + (!documentSettings.closed ? " icon-check" : " empty"),
				template: (
					<>
						<a href={listItem.id} className={classList.join(" ")} role="menuitem" data-pr-position={"left"}
						   onClick={
							   (event) => {
								   event.preventDefault();

								   if (!event.shiftKey && !event.ctrlKey) {
									   documentManager.open(listItem.id!);
									   return;
								   }

								   if (!documentSettings.selected) {
									   documentManager.select(listItem.id!, false, true);
								   } else {
									   documentManager.unselect(listItem.id!, true);
								   }
							   }
						   }>
							<span onClick={(event) => {
								event.stopPropagation(); // stop document selection
								event.preventDefault();

								documentSettings.closed = !documentSettings.closed;

								if (documentSettings.closed && documentSettings.selected) {
									this.props.context.documentManager.unselect(
										portalDocument.getSourceDocumentId()!
									);

									if (documentSelection.length === 1 &&
										this.props.contentMode === ContentMode.VIEWER) {
										for (let item of sortedItems) {
											if (!item.settings.closed && !item.settings.selected) {
												documentManager.open(item.id!);
												break;
											}
										}
									}
								}

								this.props.context.documentManager.updateGlobalDocumentList();
							}}>
								<Checkbox checked={!documentSettings.closed} style={{marginRight: "5px"}}/>
							</span>
							<span className="p-menuitem-text" style={{
								flexGrow: 1
							}}>
								{portalDocument.getFileName()}
							</span>
							{portalDocument.isArchive() && !portalDocument.isPortfolio() &&
								<MenuButton {...this.props} icon="icon icon-archive small"
											tooltip={Translate.value("document_bar.archive", {ns: "menu"})}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentManager.open(portalDocument.getSourceDocumentId()!);

													if (PdfaDialogDefinition.isEnabled(this.props.context.info)) {
														this.props.context.getPage().setState({
															showPdfaDialog: true,
															pdfaParameter: undefined
														});
													}
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
							{portalDocument.hasSignature() && !portalDocument.isPortfolio() &&
								<MenuButton {...this.props} icon="icon icon-signature small"
											tooltip={Translate.value("document_bar.signature", {ns: "menu"})}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentSettings.sidebarLeftMode = SidebarLeftMode.SIGNATURES;
													documentManager.open(portalDocument.getSourceDocumentId()!);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
							{(portalDocument.hasPasswordProtection() || portalDocument.hasCertificateProtection()) &&
								<MenuButton {...this.props} icon="icon icon-locked"
											tooltip={
												portalDocument.hasPasswordProtection() ?
													Translate.value(
														"document_bar.security.password", {ns: "menu"}
													) :
													portalDocument.hasCertificateProtection() ?
														Translate.value(
															"document_bar.security.certificate", {ns: "menu"}
														) :
														undefined
											}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentSettings.sidebarRightMode = SidebarRightMode.SECURITY;
													documentManager.open(portalDocument.getSourceDocumentId()!);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
							{portalDocument.getAttachments().length > 0 && !portalDocument.isPortfolio() &&
								<MenuButton {...this.props} icon="icon icon-attachment small"
											tooltip={Translate.value(
												"document_bar.attachment", {ns: "menu"}
											)}
											style={{
												background: "none",
												border: "none",
												minWidth: "1em",
												padding: 0
											}}
											onClick={async (event: React.MouseEvent) => {
												event.stopPropagation(); // stop document selection
												event.preventDefault();

												let command: () => Promise<void> = async () => {
													documentSettings.sidebarLeftMode = SidebarLeftMode.ATTACHMENTS;
													documentManager.open(portalDocument.getSourceDocumentId()!);
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}}/>
							}
						</a>
						{
							(
								(this.props.contentMode === ContentMode.VIEWER && !documentSettings.selected) ||
								this.props.contentMode !== ContentMode.VIEWER
							) &&
							<Tooltip target={".preview-" + tooltipId} className="tooltip-preview delay"
									 appendTo={document.getElementById("root")}>
								<div className="tooltip-content">
									<Preview {...this.props} portalDocument={Immutable.from(listItem)}/>
								</div>
							</Tooltip>
						}
					</>
				)
			});
		}

		if (dropdownItems.length > 0) {
			dropdownItems = [
				{separator: true},
				...dropdownItems
			];
		}

		return (
			<>
				{this.renderMobile()}
				<div className="document-bar" data-help="global.menu.documents" data-position="bottom">
					<div className="group left" ref={this.buttonsLeftElement}>
						<MenuButton {...this.props} icon="icon icon-home"
									tooltip={Translate.value("document_bar.home", {ns: "menu"})}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											this.props.context.getPage().setState({
												contentMode: ContentMode.STARTSCREEN
											});
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}
									className={this.props.contentMode === ContentMode.STARTSCREEN ? "active" : ""}/>
						<MenuButton {...this.props} icon="icon icon-upload small"
									disabled={!UploadDialogDefinition.isEnabled(this.props.context.info)}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											this.props.context.getPage().setState({showUploadDialog: true})
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}
									tooltip={Translate.value("document_bar.upload", {ns: "menu"})}/>
						<MenuButton {...this.props} icon="icon icon-left" disabled={!this.state.sliderNeeded}
									tooltip={Translate.value("document_bar.previous", {ns: "menu"})}
									onClick={() => this.moveSlider(SliderDirection.LEFT)}/>
					</div>
					<div className="group right" ref={this.buttonsRightElement}>
						<MenuButton {...this.props} icon="icon icon-right" disabled={!this.state.sliderNeeded}
									tooltip={Translate.value("document_bar.next", {ns: "menu"})}
									tooltipOptions={{position: 'left'}}
									onClick={() => this.moveSlider(SliderDirection.RIGHT)}/>
						<MenuButton {...this.props} icon="icon icon-angle-down" tooltipOptions={{position: 'left'}}
									tooltip={Translate.value("document_bar.close.tooltip", {ns: "menu"})}
									menuItems={[
										{
											label: Translate.value("document_bar.close.all", {ns: "menu"}),
											command: async () => {
												let command: () => Promise<void> = async () => {
													for (let listItem of this.props.portalDocuments.get()) {
														let portalDocument: PortalDocument = documentManager.findDocument(
															listItem.id!
														);
														let documentSettings: DocumentSettings = portalDocument.getSettings();
														documentSettings.closed = true;

														if (documentSettings.selected) {
															this.props.context.documentManager.unselect(listItem.id!);
														}
													}

													if (this.props.contentMode === ContentMode.VIEWER) {
														this.props.context.getPage().setState({
															contentMode: ContentMode.STARTSCREEN
														});
													}

													this.props.context.documentManager.updateGlobalDocumentList();
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}
										},
										{
											label: Translate.value("document_bar.close.selected", {ns: "menu"}),
											disabled: documentSelection.length === 0,
											command: async () => {
												let command: () => Promise<void> = async () => {
													for (let listItem of documentSelection) {
														let portalDocument: PortalDocument = documentManager.findDocument(
															listItem.id!
														);
														let documentSettings: DocumentSettings = portalDocument.getSettings();
														documentSettings.closed = true;

														this.props.context.documentManager.unselect(listItem.id!);

														if (documentSelection.length === 1 &&
															this.props.contentMode === ContentMode.VIEWER) {
															for (let item of sortedItems) {
																if (!item.settings.closed && !item.settings.selected) {
																	documentManager.open(item.id!);
																	break;
																}
															}
														}
													}

													if (documentSelection.length === 0 &&
														this.props.contentMode === ContentMode.VIEWER) {
														this.props.context.getPage().setState({
															contentMode: ContentMode.STARTSCREEN
														});
													}

													this.props.context.documentManager.updateGlobalDocumentList();
												}

												if (DocumentSettings.checkActiveMouseFunction(this.props)) {
													DocumentSettings.confirmResetMouseFunction(this.props, command);
													return;
												}

												await command();
											}
										},
										{
											label: Translate.value("document_bar.close.other", {ns: "menu"}),
											disabled: documentSelection.length === 0,
											command: () => {
												for (let listItem of sortedItems) {
													let portalDocument: PortalDocument = documentManager.findDocument(
														listItem.id!
													);
													let documentSettings: DocumentSettings = portalDocument.getSettings();

													if (!documentSettings.selected) {
														documentSettings.closed = true;
													}
												}

												this.props.context.documentManager.updateGlobalDocumentList();
											}
										},
										...dropdownItems
									]}/>
						<DocumentFilterComponent {...this.props}/>
					</div>
					<div className="documents" ref={this.documentSliderContainerElement}>
						<div className="document-slider" ref={this.documentSliderElement}
							 style={{left: this.state.sliderPosition + LengthUnit.PIXEL}}>
							{items}
						</div>
					</div>
				</div>
			</>
		);
	}
}

const DocumentBar = React.memo(DocumentBarDefinition,
	(prevProps: Readonly<DocumentBarProperties>, nextProps: Readonly<DocumentBarProperties>) => {
		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (!nextProps.portalDocuments.equals(prevProps.portalDocuments)) {
			return false;
		}

		if (!nextProps.documentSelection.equals(prevProps.documentSelection)) {
			return false;
		}

		if (!nextProps.documentFilter.equals(prevProps.documentFilter)) {
			return false;
		}

		if (!nextProps.documentSorting.equals(prevProps.documentSorting)) {
			return false;
		}

		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		return true;
	}
);

export default DocumentBar;